import dayjs from "dayjs";
import { useShallow } from "zustand/react/shallow";

import useCalendarStore from "@/store/calendarStore";
import type { DailyReservation, DailyReservationData } from "@/types/reservation";
import { cn } from "@/utils/cn";

const statusColor: Record<string, string> = {
  approve: "bg-green-100 border-green-300 text-green-700",
  request: "bg-amber-50 border-amber-200 text-amber-700",
  reject: "bg-red-50 border-red-200 text-red-600",
  cancel: "bg-gray-50 border-gray-200 text-gray-400",
};

const statusLabel: Record<string, string> = {
  approve: "승인",
  request: "대기",
  reject: "반려",
  cancel: "취소",
};

interface VehicleDailyViewProps {
  onSelect?: (reservation: DailyReservationData | null) => void;
  selectedReservation?: DailyReservationData | null;
}

const VehicleDailyView = ({ onSelect, selectedReservation }: VehicleDailyViewProps) => {
  const [date, monthlyReservations] = useCalendarStore(
    useShallow(state => [state.date, state.monthlyReservations]),
  );

  const dailyMatch = monthlyReservations?.find(
    (r: DailyReservation) => dayjs(r.day).isSame(date, "day"),
  );
  const reservations: DailyReservationData[] = dailyMatch?.data ?? [];

  const handleClick = (e: React.MouseEvent, r: DailyReservationData) => {
    e.stopPropagation();
    if (selectedReservation?.rsvtId === r.rsvtId) {
      onSelect?.(null);
    } else {
      onSelect?.(r);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200">
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
        <h4 className="text-sm font-medium text-gray-700">
          {date.format("YYYY년 M월 D일 (dd)")} 차량 예약
        </h4>
      </div>

      <div className="p-4" onClick={() => onSelect?.(null)}>
        {reservations.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-gray-400">
            예약 내역이 없습니다.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {reservations.map((r) => {
              const colorCls = statusColor[r.sttCd] || statusColor.approve;
              const isSelected = selectedReservation?.rsvtId === r.rsvtId;

              return (
                <div
                  key={r.rsvtId}
                  onClick={(e) => handleClick(e, r)}
                  className={cn(
                    "cursor-pointer rounded-lg border p-4 transition-all",
                    colorCls,
                    isSelected
                      ? "ring-2 ring-primary shadow-lg scale-[1.01]"
                      : "hover:-translate-y-px hover:shadow-md",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{r.plcNm || "차량"}</span>
                      <span className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-medium",
                        statusColor[r.sttCd] || "",
                      )}>
                        {statusLabel[r.sttCd] || r.sttCd}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">전일</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    <span className="font-medium">목적: </span>{r.useCnts || "-"}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    <span className="font-medium">신청자: </span>{r.userName || "-"} {r.telNo ? `(${r.telNo})` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleDailyView;
